import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FormProvider } from "react-hook-form";
import { toast } from "sonner";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { pricingTableApi } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import usePricingTableForm from "@/hooks/usePricingTableForm";
import { PricingTableTransactionFees } from "@/components/pricing/PricingTableTransactionFees";
import { PricingTableDocumentFees } from "@/components/pricing/PricingTableDocumentFees";
import PricingTableDeliveryFees from "@/components/pricing/PricingTableDeliveryFees";
import { PricingTableCustomFormFees } from "@/components/pricing/PricingTableCustomFormFees";
import { PricingTableBasicInfo } from "@/components/pricing/PricingTableBasicInfo";
import { pricingTableFormSchema } from '@/schemas/pricingTableSchema';

const PricingTableDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRoles, selectedRole, loading, permissionsLoaded } = useAuth();

  // Determine if user has platform role
  const hasPlatformRole =
    userRoles.platform.length > 0 && selectedRole?.type === "platform";

  // Set default tab based on user role
  const [activeTab, setActiveTab] = useState(() => {
    // If user doesn't have platform role, default to documents tab
    return hasPlatformRole ? "fees" : "documents";
  });

  const [formErrors, setFormErrors] = useState<string[]>([]);

  // Update active tab when role changes
  useEffect(() => {
    if (!hasPlatformRole && activeTab === "fees") {
      setActiveTab("documents");
    }
  }, [hasPlatformRole, activeTab]);

  // Fetch the pricing table data
  const { data: pricingTable, isLoading: loadingPricingTable } = useQuery({
    queryKey: ["pricing-table", id],
    queryFn: () => pricingTableApi.getById(Number(id)),
    enabled: !!id,
  });

  const {
    form,
    documentFeeFields,
    transactionFeeArray,
    feeApportionParties,
    documentTypes,
    loadingParties,
    loadingDocumentTypes,
    appendDocumentFee,
    removeDocumentFee,
    onSubmit,
    updateMutation,
    isEditing,
    validateApportionTotals,
  } = usePricingTableForm({
    pricingTableId: Number(id)
    // Don't pass initialData here - we'll handle it in the useEffect
  });

  // Initialize form with the pricing table data when it's loaded
  useEffect(() => {
    if (pricingTable) {
      // Transform delivery_fees to ensure all required fields are present
      // Filter out delivery fees with empty fees arrays since they're incomplete
      const transformedDeliveryFees = (pricingTable.delivery_fees || [])
        .filter((deliveryFee: any) => {
          // Only keep delivery fees that have at least one fee
          return deliveryFee.fees && deliveryFee.fees.length > 0;
        })
        .map((deliveryFee: any) => {
          const transformedFees = (deliveryFee.fees || []).map((fee: any) => {
            // Ensure fee_id is a proper number
            const feeId = fee.fee_id ? Number(fee.fee_id) : Math.floor(Math.random() * 1000000);
            
            // Ensure name is a non-empty string
            let feeName = fee.name;
            if (!feeName || typeof feeName !== 'string' || feeName.trim() === '') {
              feeName = `${deliveryFee.document_type_name || 'Document'} - ${deliveryFee.delivery_time_name || 'Delivery'}`;
            }
            
            return {
              fee_id: feeId,
              name: feeName,
              amount: Number(fee.amount) || 0,
              multiply_by_applicants: Boolean(fee.multiply_by_applicants),
              married_count_as_one: Boolean(fee.married_count_as_one),
              apportionment: fee.apportionment || []
            };
          });
          
          return {
            ...deliveryFee,
            fees: transformedFees
          };
        });

      // Map API data to form values
      const formValues = {
        name: pricingTable.name,
        description: pricingTable.description || "",
        transaction_fees: pricingTable.transaction_fees || [],
        document_fees: pricingTable.document_fees || [],
        bundles: pricingTable.bundles || [],
        delivery_fees: transformedDeliveryFees,
        custom_form_fees: (pricingTable.custom_form_fees || []).map((customFormFee: any) => ({
          ...customFormFee,
          apportionment: customFormFee.apportionment || (feeApportionParties || []).map(party => ({
            apportionment_party_id: party.id,
            amount: 0
          }))
        }))
      };

      form.reset(formValues);
    }
  }, [pricingTable, form]);

  // We'll listen to form changes to clear errors when apportionment values change
  useEffect(() => {
    const subscription = form.watch(() => {
      // If there are errors, validate again to see if they've been fixed
      if (formErrors.length > 0) {
        const currentValues = form.getValues();
        const currentErrors = validateApportionTotals(currentValues);

        // Only update if the errors have changed
        if (JSON.stringify(currentErrors) !== JSON.stringify(formErrors)) {
          setFormErrors(currentErrors);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form, formErrors, validateApportionTotals]);

  const handleSubmit = (data: any) => {
    // Clear previous errors
    setFormErrors([]);

    // Validate form data
    const errors = validateApportionTotals(data);
    
    if (errors.length > 0) {
      setFormErrors(errors);
      return;
    }

    // If no validation errors, proceed with submission
    onSubmit(data);
  };

  const handleBackClick = () => {
    navigate("/backoffice/pricing");
  };

  // Show loading state while permissions are loading
  if (loading || !permissionsLoaded) {
    return (
      <BackofficeLayout>
        <div className="container mx-auto py-6 flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          <span className="ml-2">Loading permissions...</span>
        </div>
      </BackofficeLayout>
    );
  }

  // Show message if no role is selected
  if (!selectedRole) {
    return (
      <BackofficeLayout>
        <div className="text-center py-8">
          <h3 className="text-lg font-medium">No role selected</h3>
          <p className="text-sm text-muted-foreground">
            Please select a role to view pricing table details.
          </p>
        </div>
      </BackofficeLayout>
    );
  }

  if (loadingPricingTable) {
    return (
      <BackofficeLayout>
        <div className="container mx-auto py-6 flex items-center justify-center h-screen">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="icon" onClick={handleBackClick}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              Edit Pricing Table
            </h1>
          </div>
        </div>

        {/* Error Messages Display */}
        {formErrors.length > 0 && (
          <div className="bg-destructive/15 border border-destructive rounded-md p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-destructive mr-2 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-destructive">
                  Please fix the following errors:
                </h3>
                <ul className="mt-1 text-sm list-disc pl-5 space-y-1">
                  {formErrors.map((error, index) => (
                    <li key={index} className="text-destructive">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <FormProvider {...form}>
          <form
            
            id="pricing-table-form"
            
            onSubmit={form.handleSubmit(handleSubmit)}
            
            className="space-y-8"
          
          >
            <PricingTableBasicInfo control={form.control} />
            
            
              <Tabs defaultValue="fees" value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4">
                {hasPlatformRole ? <TabsTrigger value="fees">Transaction Fees</TabsTrigger> : null}
                  <TabsTrigger value="documents">Document Fees</TabsTrigger>
                  <TabsTrigger value="delivery">Delivery Fees</TabsTrigger>
                  <TabsTrigger value="custom-forms">
                    Custom Form Fees
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="fees" className="space-y-4 pt-4">
                  <PricingTableTransactionFees
                    feesApportionParties={feeApportionParties}
                  />
                </TabsContent>

                <TabsContent value="documents" className="space-y-4 pt-4">
                  <PricingTableDocumentFees
                    feesApportionParties={feeApportionParties}
                    documentTypes={documentTypes || []}
                  />
                </TabsContent>

                <TabsContent value="delivery" className="space-y-4 pt-4">
                  <PricingTableDeliveryFees
                    documentTypes={documentTypes || []}
                    feesApportionParties={feeApportionParties}
                />
                </TabsContent>

              <TabsContent value="custom-forms" className="space-y-4 pt-4">
                <PricingTableCustomFormFees
                  documentTypes={documentTypes || []}
                  feesApportionParties={feeApportionParties}
                />
              </TabsContent>
              </Tabs>
              
              <div className="flex justify-end pt-6">
                <Button 
                  type="submit" 
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
             
          </form>
        </FormProvider>
      </div>
    </BackofficeLayout>
  );
};

export default PricingTableDetail;
